"use client";

import { Capacitor } from "@capacitor/core";
import { useEffect, useRef } from "react";
import { useCurrentLocation } from "@/hooks/use-current-location";
import { captureClientEvent } from "@/lib/analytics/client";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import { resolveAndroidMirrorChannel } from "@/lib/native/hiive-push-bridge";
import {
  foregroundNotificationId,
  PushReplayGuard,
} from "@/lib/native/push-replay-guard";

/**
 * Foreground push handling for native shells.
 *
 * `PushNotifications.presentationOptions` is now empty, so iOS presents
 * nothing of its own — not a banner and, crucially, not a sound. It used to
 * carry "sound", which Capacitor's willPresent returns for EVERY foreground
 * push before this listener runs: the device rang even for the conversation
 * already open on screen, and rang a second time for the mirror below.
 * Android's HiiveMessagingService suppresses system notifications while
 * foregrounded. So this hook is the single decision point:
 *
 * - push for the chat currently on screen → nothing (polling renders it)
 * - any other push → mirrored as a local notification, which now carries the
 *   sound, the thread id and the action category the push itself had, so the
 *   mirror behaves like the real thing instead of a bare banner.
 */
export function useForegroundPush() {
  const { pathname } = useCurrentLocation();
  const pathnameRef = useRef(pathname);

  useEffect(() => {
    pathnameRef.current = pathname;
  }, [pathname]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let storage: Storage | undefined;
    try {
      storage = window.localStorage;
    } catch {
      /* Restricted storage. */
    }
    const replayGuard = new PushReplayGuard(storage);
    let cancelled = false;
    let removeListener: (() => void) | undefined;

    void (async () => {
      const { PushNotifications } = await import(
        "@capacitor/push-notifications"
      );

      const handle = await PushNotifications.addListener(
        "pushNotificationReceived",
        (notification) => {
          const data = (notification.data ?? {}) as Record<string, unknown>;
          if (
            data.source === "campaign_manager" &&
            typeof data.deliveryToken === "string"
          ) {
            void fetch("/api/campaign-manager/delivered", {
              method: "POST",
              headers: { "content-type": "application/json" },
              body: JSON.stringify({ token: data.deliveryToken }),
              keepalive: true,
            }).catch(() => undefined);
          }
          const chatId = data.chatId != null ? String(data.chatId) : null;
          // iOS hands over the whole APNs userInfo, where the category and
          // thread id live INSIDE `aps` (buildApnsPayload); Android's data
          // payload carries them top-level. Reading only the top level left
          // every iOS mirror without its Reply / Show ticket actions and
          // ungrouped, and every iOS analytics event without a category.
          const aps =
            data.aps && typeof data.aps === "object"
              ? (data.aps as Record<string, unknown>)
              : {};
          const categoryValue = data.category ?? aps.category;
          const threadValue = data.threadId ?? aps["thread-id"];

          // Delivery-side half of the push funnel. Paired with
          // PUSH_NOTIFICATION_TAPPED this gives a real open rate per
          // notification type — the number that decides whether a campaign
          // was worth sending.
          captureClientEvent(ANALYTICS_EVENTS.PUSH_NOTIFICATION_RECEIVED, {
            notification_category:
              typeof categoryValue === "string" ? categoryValue : undefined,
            notification_type:
              typeof data.notificationType === "string"
                ? data.notificationType
                : undefined,
            app_state: "foreground",
            has_chat_id: chatId != null,
          });

          // Android chat/message pushes are rendered by HiivePushNotificationRenderer
          // (rich MessagingStyle, real avatar, WhatsApp-style stacking) in both
          // foreground and background — never mirror them as a plain local
          // notification here, which would duplicate and not stack.
          const category =
            typeof categoryValue === "string" ? categoryValue : "";
          const notificationType =
            typeof data.notificationType === "string"
              ? data.notificationType
              : "";
          const dataUrl = typeof data.url === "string" ? data.url : "";
          const isMessage =
            category === "hiive_message" ||
            notificationType === "direct_message" ||
            dataUrl.includes("/chat/");
          if (isMessage && Capacitor.getPlatform() === "android") return;
          if (
            isMessage &&
            Capacitor.getPlatform() === "ios" &&
            (
              window as Window & {
                __HIIVE_NATIVE_CHAT_PRESENTATION__?: boolean;
              }
            ).__HIIVE_NATIVE_CHAT_PRESENTATION__
          )
            return;
          const notificationTitle =
            typeof notification.title === "string"
              ? notification.title.trim()
              : "";
          const notificationBody =
            typeof notification.body === "string"
              ? notification.body.trim()
              : "";
          const dataTitle =
            typeof data.title === "string" ? data.title.trim() : "";
          const dataBody =
            typeof data.body === "string" ? data.body.trim() : "";
          const title = notificationTitle || dataTitle || "Hiive";
          const body = notificationBody || dataBody;
          const url = typeof data.url === "string" ? data.url : undefined;

          // The open chat already shows this message via polling.
          if (chatId && pathnameRef.current === `/chat/${chatId}`) {
            return;
          }

          const threadId =
            typeof threadValue === "string" ? threadValue : undefined;
          const android = Capacitor.getPlatform() === "android";

          const eventId =
            data.notificationId != null
              ? String(data.notificationId)
              : notification.id;
          if (eventId && !replayGuard.claim(eventId)) return;
          void (async () => {
            const { LocalNotifications } = await import(
              "@capacitor/local-notifications"
            );
            // The push's own Android channel (Bookings, Activity, Promotions…)
            // when this binary has it; "hiive-default" on older binaries,
            // where posting to an unknown channel would drop the mirror.
            const channelId = android
              ? await resolveAndroidMirrorChannel(data)
              : undefined;
            await LocalNotifications.schedule({
              notifications: [
                {
                  id: eventId
                    ? foregroundNotificationId(eventId)
                    : Date.now() % 2_147_483_647,
                  title,
                  body,
                  // Without this the mirror is silent: presentationOptions no
                  // longer rings, and LocalNotifications leaves content.sound
                  // nil unless a name is given.
                  sound: "default",
                  // Groups successive messages into one conversation card the
                  // way the real push's aps thread-id does, instead of a new
                  // banner per message.
                  ...(threadId ? { threadIdentifier: threadId } : {}),
                  // Maps to content.categoryIdentifier, which is what puts the
                  // Reply/Open actions from AppDelegate's categories on the
                  // mirror. Nothing calls registerActionTypes, so the
                  // natively-registered categories are still the live set.
                  ...(category ? { actionTypeId: category } : {}),
                  // chatId rides along so the native inline-reply handler can
                  // resolve the conversation from a mirrored notification too.
                  extra: { url, chatId, notificationId: data.notificationId },
                  // Android: the same channel and status-bar glyph as natively
                  // rendered pushes. Without them the mirror landed on
                  // Capacitor's DEFAULT-importance channel (no heads-up) with
                  // the stock info icon.
                  ...(channelId
                    ? {
                        channelId,
                        smallIcon: "ic_notification",
                      }
                    : {}),
                },
              ],
            });
          })().catch(() => {
            if (eventId) replayGuard.release(eventId);
          });
        },
      );

      if (cancelled) {
        void handle.remove();
        return;
      }
      removeListener = () => void handle.remove();
    })();

    return () => {
      cancelled = true;
      removeListener?.();
    };
  }, []);
}
